import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { 
  House, 
  Users, 
  Notebook, 
  ClipboardText, 
  CameraPlus,
  CurrencyDollar,
  ChartBar,
  SignOut,
  Clock,
  UserGear,
  Table,
  Tray,
  ArrowSquareOut,
  Buildings
} from '@phosphor-icons/react';

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const username = localStorage.getItem('username');
  const userRole = localStorage.getItem('role') || 'admin';
  const userServiceId = localStorage.getItem('user_service_id');
  const userServiceName = localStorage.getItem('user_service_name');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [services, setServices] = useState([]);
  const [serviceFilter, setServiceFilter] = useState(localStorage.getItem('service_filter') || 'all');

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!userServiceId) {
      api.get('/services', { params: { service_id: 'all' } })
        .then((res) => setServices(res.data))
        .catch(() => {});
    }
  }, [userServiceId]);

  const handleServiceChange = (e) => {
    const val = e.target.value;
    setServiceFilter(val);
    localStorage.setItem('service_filter', val);
    window.location.reload();
  };

  const allNavItems = [
    { path: '/', icon: House, label: 'Dashboard', testId: 'nav-dashboard', roles: ['admin', 'admin_nofee', 'attendance_only'] },
    { path: '/mark-attendance', icon: CameraPlus, label: 'Mark Attendance', testId: 'nav-mark-attendance', roles: ['admin', 'admin_nofee', 'attendance_only'] },
    { path: '/students', icon: Users, label: 'Students', testId: 'nav-students', roles: ['admin', 'admin_nofee'] },
    { path: '/students-sheet', icon: Table, label: 'Spreadsheet', testId: 'nav-students-sheet', roles: ['admin', 'admin_nofee'] },
    { path: '/submissions', icon: Tray, label: 'Submissions', testId: 'nav-submissions', roles: ['admin', 'admin_nofee'] },
    { path: '/batches', icon: Notebook, label: 'Batches', testId: 'nav-batches', roles: ['admin', 'admin_nofee'] },
    { path: '/attendance', icon: ClipboardText, label: 'Attendance', testId: 'nav-attendance', roles: ['admin', 'admin_nofee'] },
    { path: '/payments', icon: CurrencyDollar, label: 'Payments', testId: 'nav-payments', roles: ['admin', 'admin_nofee'] },
    { path: '/reports', icon: ChartBar, label: 'Reports', testId: 'nav-reports', roles: ['admin'] },
    { path: '/services', icon: Buildings, label: 'Services', testId: 'nav-services', roles: ['admin'] },
    { path: '/user-management', icon: UserGear, label: 'User Management', testId: 'nav-user-management', roles: ['admin'] },
  ];

  const navItems = allNavItems.filter(item => item.roles.includes(userRole));

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('service_filter');
    localStorage.removeItem('user_service_id');
    localStorage.removeItem('user_service_name');
    navigate('/login');
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="flex h-screen bg-slate-950">
      <aside className="w-64 bg-slate-950 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <img src="/mrva-logo.png" alt="MRVA Productions" className="h-10 object-contain" />
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.testId}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                  isActive
                    ? 'bg-slate-900 text-blue-400 border border-slate-800'
                    : 'text-slate-400 hover:text-white hover:bg-slate-900/50'
                }`}
              >
                <Icon size={20} weight="duotone" />
                <span className="font-medium text-sm">{item.label}</span>
              </Link>
            );
          })}

          <a
            href="/register"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-form"
            className="flex items-center gap-3 px-4 py-3 rounded-md transition-all text-slate-400 hover:text-white hover:bg-slate-900/50"
          >
            <ArrowSquareOut size={20} weight="duotone" />
            <span className="font-medium text-sm">Form</span>
          </a>
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-slate-300">{username}</p>
              <p className="text-xs text-slate-500">Admin</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white rounded-md transition-colors border border-slate-800"
          >
            <SignOut size={18} weight="duotone" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="sticky top-0 z-10 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
          <div className="flex items-center justify-between px-6 sm:px-8 lg:px-12 py-3">
            <div className="flex items-center gap-2" data-testid="service-scope">
              <Buildings size={18} weight="duotone" className="text-blue-400" />
              {userServiceId ? (
                <span className="text-sm font-semibold text-slate-200" data-testid="scoped-service-name">{userServiceName || 'My Service'}</span>
              ) : (
                <select
                  value={serviceFilter}
                  onChange={handleServiceChange}
                  data-testid="service-filter-select"
                  className="bg-slate-900 border border-slate-800 rounded-md text-sm text-slate-200 px-3 py-1.5 focus:outline-none focus:border-blue-500 cursor-pointer"
                >
                  <option value="all">All Services</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex items-center gap-3" data-testid="live-clock">
              <Clock size={18} weight="duotone" className="text-blue-400" />
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-50 tracking-tight" data-testid="live-time">{formatTime(currentTime)}</p>
                <p className="text-xs text-slate-400" data-testid="live-date">{formatDate(currentTime)}</p>
              </div>
            </div>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
}

export default Layout;
